import {
  AuditLogEvents,
  isSomeEnum,
  NO_VALUE_DISPLAY,
  normalizeAuditLogs,
  RideCrudModeKey,
  type FormatDateTimeNormalizedFn,
  type IAuditLogDto,
  type IAuditLogNormalized,
  type IRideInvestigationHistoryEntryDto,
} from "@ryde/shared";

import type {
  IInvestigationAuditChange,
  IInvestigationAuditChanges,
  IInvestigationAuditColumn,
} from "./investigation.models";

/**
 * The audit rows behind the history entries, in the shape the ride history drawer works on. Only the id, the event
 * and the written properties drive the replay; the other fields are filled in to complete the shape.
 */
export const toAuditLogRows = ({
  entries,
  rideId,
}: {
  entries: IRideInvestigationHistoryEntryDto[];
  rideId: number;
}): IAuditLogDto[] =>
  entries.flatMap(entry => {
    const { audit } = entry;
    const eventName = audit?.eventName;

    if (!audit?.properties || !isSomeEnum(AuditLogEvents)(eventName)) {
      return [];
    }

    const row: IAuditLogDto = {
      id: audit.logId,
      service: "",
      eventName,
      subjectName: "RideEntity",
      subjectId: String(rideId),
      entity: RideCrudModeKey.uiUnit,
      causerType: "",
      causerName: entry.actorName ?? "",
      causerId: "",
      companyId: 0,
      properties: audit.properties,
      metadata: { diffVersion: audit.diffVersion },
      createdAt: entry.timestamp,
      updatedAt: entry.timestamp,
      deletedAt: null,
    };

    return [row];
  });

const compareColumns = ({
  columns,
  isChanged,
  item,
  itemBefore,
  hasCreationRow,
}: {
  columns: IInvestigationAuditColumn[];
  isChanged: (after: string, before: string) => boolean;
  item: IAuditLogNormalized;
  itemBefore: IAuditLogNormalized;
  hasCreationRow: boolean;
}): IInvestigationAuditChange[] =>
  columns.flatMap(column => {
    const after = column.read(item);
    const before = column.read(itemBefore);

    if (!isChanged(after, before)) {
      return [];
    }

    const change: IInvestigationAuditChange = {
      key: column.key,
      labelKey: column.labelKey,
      // Without the creation row, a value no loaded row wrote before is unknown, not empty.
      before:
        before === NO_VALUE_DISPLAY && !hasCreationRow ? undefined : before,
      after,
    };

    return [change];
  });

/**
 * What each audited change did, worked out as the ride history drawer does it: the rows are replayed into the ride's
 * state after every change (`normalizeAuditLogs`), and each drawer column is compared with its value one change
 * earlier. `details` is the drawer's own field-by-field list for the row. Keyed by audit row id.
 */
export const buildAuditChanges = ({
  entries,
  rideId,
  columns,
  isChanged,
  formatDateTimeNormalized,
}: {
  entries: IRideInvestigationHistoryEntryDto[];
  rideId: number;
  columns: IInvestigationAuditColumn[];
  isChanged: (after: string, before: string) => boolean;
  formatDateTimeNormalized: FormatDateTimeNormalizedFn;
}): Map<number, IInvestigationAuditChanges> => {
  const items = normalizeAuditLogs({
    auditLogs: toAuditLogRows({ entries, rideId }),
    formatDateTimeNormalized,
  });
  const hasCreationRow = items.some(
    item => item.eventName === AuditLogEvents.EntityCreated,
  );

  return new Map(
    items.map((item, index) => {
      const itemBefore = index > 0 ? items[index - 1] : undefined;
      const auditChanges: IInvestigationAuditChanges = {
        changes: itemBefore
          ? compareColumns({
              columns,
              isChanged,
              item,
              itemBefore,
              hasCreationRow,
            })
          : [],
        details: item.entityProperties,
      };

      return [item.id, auditChanges];
    }),
  );
};
